/**
 * =============================================================================
 * ROASTBREW - TOOLS PAGE (Content Only)
 * =============================================================================
 *
 * Tools grouped into three avenues — For Students, For Job Seekers, For Teachers.
 * For now every tool is "Coming soon" and the page is display-only: nothing on it
 * is clickable.
 *
 * Access: gated in App.jsx (RequireFeature 'tools' → config.js FEATURE_MIN_TIER).
 * This page contains no tier logic of its own.
 *
 * Renders within AppLayout (sidebar handled by parent).
 * =============================================================================
 */

import React from 'react';
import { useTheme } from './ThemeContext';

// ── The tools ───────────────────────────────────────────────────────────────
// One entry per tool. To add a tool, add an entry and list its id under an avenue
// below. When a tool is built, give it a path and make its card a link (for now
// nothing is clickable, on purpose).
const TOOLS = {
  'college-application-process': {
    name: 'College Application Process',
    description: 'Keep deadlines, essays and applications organized in one place.',
    icon: (c) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10L12 5 2 10l10 5 10-5z" /><path d="M6 12v5c3 2 9 2 12 0v-5" />
      </svg>
    ),
  },
  'resume-builder': {
    name: 'Resume Builder',
    description: 'Turn your experience into a polished resume, one section at a time.',
    icon: (c) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8M8 17h5" />
      </svg>
    ),
  },
  'interview-prep': {
    name: 'Interview Prep',
    description: 'Rehearse common interview questions and sharpen your answers.',
    icon: (c) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /><path d="M8 9h8M8 13h5" />
      </svg>
    ),
  },
  'worksheet-creator': {
    name: 'Worksheet Creator',
    description: 'Build practice worksheets on any topic, with answer keys.',
    icon: (c) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h4" />
      </svg>
    ),
  },
};

// ── The three avenues ───────────────────────────────────────────────────────
const AVENUES = [
  {
    id: 'students',
    title: 'For Students',
    blurb: 'Plan the next step after high school.',
    tools: ['college-application-process'],
    icon: (c) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 5.5C4.5 4 8.5 4 12 6c3.5-2 7.5-2 10-.5V19c-2.5-1.5-6.5-1.5-10 .5-3.5-2-7.5-2-10-.5V5.5z" /><path d="M12 6v13.5" />
      </svg>
    ),
  },
  {
    id: 'job-seekers',
    title: 'For Job Seekers',
    blurb: 'Present yourself at your best.',
    tools: ['resume-builder', 'interview-prep'],
    icon: (c) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" /><path d="M3 13h18" />
      </svg>
    ),
  },
  {
    id: 'teachers',
    title: 'For Teachers',
    blurb: 'Prepare classroom materials in minutes.',
    tools: ['worksheet-creator'],
    icon: (c) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M8 20h8M12 16v4" /><path d="M7 9h6M7 12h4" />
      </svg>
    ),
  },
];

export default function ToolsPage({ isMobile = false, isTablet = false }) {
  const { colors, isLight } = useTheme();
  const edge = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  const tint = isLight ? 'rgba(26,26,26,0.04)' : `${colors.accent}10`;
  const tintEdge = isLight ? 'rgba(0,0,0,0.06)' : `${colors.accent}20`;
  const columns = !isMobile && !isTablet;                  // three avenues side by side on desktop

  const toolCard = (id, i) => {
    const tool = TOOLS[id];
    return (
      <div key={id} style={{
        display: 'flex', flexDirection: 'column', gap: '12px', padding: '20px',
        borderRadius: '16px', background: colors.cardBg || colors.bgAlt, border: `1px solid ${edge}`,
        animation: `fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${0.12 + i * 0.06}s both`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ width: '42px', height: '42px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: tint, border: `1px solid ${tintEdge}` }}>
            {tool.icon(colors.accent)}
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '5px 11px', borderRadius: '20px', background: tint, border: `1px solid ${tintEdge}`, fontFamily: '"DM Sans", sans-serif', fontSize: '11.5px', fontWeight: 500, letterSpacing: '0.04em', color: colors.muted, whiteSpace: 'nowrap' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.accent }} />
            Coming soon
          </span>
        </div>
        <div>
          <div style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '23px', lineHeight: 1.15, color: colors.text }}>{tool.name}</div>
          <div style={{ marginTop: '6px', fontFamily: '"DM Sans", sans-serif', fontSize: '13.5px', lineHeight: 1.55, color: colors.textSecondary || colors.muted }}>{tool.description}</div>
        </div>
      </div>
    );
  };

  let n = 0;   // running index for the staggered entrance
  return (
    <div style={{
      padding: isMobile ? '80px 16px 32px' : isTablet ? '80px 32px 32px' : '32px 48px',
      minHeight: '100vh', width: '100%', maxWidth: '100%', boxSizing: 'border-box',
      background: colors.bg, animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      <header style={{ maxWidth: '1120px', margin: isMobile ? '0 auto 24px' : '40px auto 36px' }}>
        <h1 style={{ margin: 0, fontFamily: '"Cormorant Garamond", serif', fontWeight: 300, letterSpacing: '-0.02em', fontSize: isMobile ? '34px' : '44px', color: colors.text }}>
          Tools
        </h1>
        <p style={{ margin: '8px 0 0', fontFamily: '"DM Sans", sans-serif', fontSize: '15px', lineHeight: 1.6, color: colors.muted }}>
          Practical tools for school, work, and the classroom.
        </p>
      </header>

      <div style={{
        maxWidth: '1120px', margin: '0 auto', display: 'grid',
        gridTemplateColumns: columns ? 'repeat(3, minmax(0, 1fr))' : '1fr',
        gap: columns ? '28px' : '36px', alignItems: 'start',
      }}>
        {AVENUES.map((av, ai) => (
          <section key={av.id} aria-label={av.title} style={{ animation: `fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${0.06 + ai * 0.06}s both` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingBottom: '14px', marginBottom: '16px', borderBottom: `1px solid ${edge}` }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: tint, border: `1px solid ${tintEdge}`, flexShrink: 0 }}>
                {av.icon(colors.accent)}
              </div>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0, fontFamily: '"Cormorant Garamond", serif', fontWeight: 400, fontSize: isMobile ? '25px' : '28px', lineHeight: 1.1, color: colors.text }}>{av.title}</h2>
                <div style={{ marginTop: '3px', fontFamily: '"DM Sans", sans-serif', fontSize: '13px', color: colors.muted }}>{av.blurb}</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: (isTablet && !isMobile) ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: '14px' }}>
              {av.tools.map((id) => toolCard(id, n++))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
